import { useEffect, useState, useRef, type DragEvent, type ChangeEvent } from "react";
import { toast } from "sonner";
import {
  FiFile,
  FiUpload,
  FiDownload,
  FiTrash2,
  FiLoader,
  FiX,
} from "react-icons/fi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useDocuments } from "./_hooks/useDocuments";
import {
  type DocumentCategory,
  documentCategoryLabels,
  documentStatusConfig,
} from "./_types/portal.types";
import { CardSkeleton } from "@/components/common";
import { cn } from "@/lib/utils";

const categories: DocumentCategory[] = [
  "id_verification",
  "income",
  "bank_statements",
  "contracts",
  "other",
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "heic", "docx", "xlsx", "csv"];

export function DocumentsPage() {
  const {
    documents,
    isLoading,
    fetchDocuments,
    uploadDocument,
    deleteDocument,
    downloadDocument,
  } = useDocuments();

  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory | "all">("all");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadCategory, setUploadCategory] = useState<DocumentCategory>("other");
  const [uploadDescription, setUploadDescription] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Filter documents by category
  const filteredDocuments =
    selectedCategory === "all"
      ? documents
      : documents.filter((d) => d.category === selectedCategory);

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // Validate file
  const validateFile = (file: File): string | null => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
      return `Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return "File too large. Maximum size is 10MB";
    }
    return null;
  };

  // Handle file selection
  const handleFileSelect = (file: File) => {
    const error = validateFile(file);
    if (error) {
      toast.error(error);
      return;
    }
    setUploadFile(file);
  };

  // Handle file input change
  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Handle drag and drop
  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error("Please select a file");
      return;
    }

    setIsUploading(true);

    const result = await uploadDocument(
      uploadFile,
      uploadCategory,
      uploadDescription || undefined
    );

    setIsUploading(false);

    if (result.success) {
      toast.success("Document uploaded successfully");
      setUploadModalOpen(false);
      resetUploadForm();
    } else {
      toast.error(result.error || "Upload failed");
    }
  };

  // Reset upload form
  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadCategory("other");
    setUploadDescription("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    const result = await deleteDocument(id);

    if (result.success) {
      toast.success("Document deleted");
    } else {
      toast.error(result.error || "Delete failed");
    }

    setDeleteConfirmId(null);
  };

  // Handle download
  const handleDownload = async (doc: typeof documents[0]) => {
    try {
      await downloadDocument(doc);
      toast.success("Download started");
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-medium text-primary">
            Documents
          </h1>
          <p className="text-muted-foreground mt-1">
            Upload and manage your documents
          </p>
        </div>
        <Button className="btn-pill" onClick={() => setUploadModalOpen(true)}>
          <FiUpload className="mr-2 h-4 w-4" />
          Upload Document
        </Button>
      </div>

      {/* Category filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <Button
          variant={selectedCategory === "all" ? "default" : "outline"}
          size="sm"
          className="btn-pill whitespace-nowrap"
          onClick={() => setSelectedCategory("all")}
        >
          All ({documents.length})
        </Button>
        {categories.map((cat) => {
          const count = documents.filter((d) => d.category === cat).length;
          return (
            <Button
              key={cat}
              variant={selectedCategory === cat ? "default" : "outline"}
              size="sm"
              className="btn-pill whitespace-nowrap"
              onClick={() => setSelectedCategory(cat)}
            >
              {documentCategoryLabels[cat]} ({count})
            </Button>
          );
        })}
      </div>

      {/* Document list */}
      <Card>
        <CardHeader>
          <CardTitle>
            {selectedCategory === "all"
              ? "All Documents"
              : documentCategoryLabels[selectedCategory]}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FiFile className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">
                {selectedCategory === "all"
                  ? "No documents yet"
                  : `No ${documentCategoryLabels[selectedCategory].toLowerCase()} documents`}
              </p>
              <Button
                className="mt-4 btn-pill"
                onClick={() => setUploadModalOpen(true)}
              >
                <FiUpload className="mr-2 h-4 w-4" />
                Upload Document
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/50 gap-4"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="p-2 bg-background rounded shrink-0">
                      <FiFile className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">
                        {doc.originalFilename}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(doc.fileSize)} &bull;{" "}
                        {new Date(doc.createdAt).toLocaleDateString()}
                      </p>
                      {doc.description && (
                        <p className="text-xs text-muted-foreground truncate mt-1">
                          {doc.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Badge
                      variant="secondary"
                      className={cn(
                        "hidden sm:inline-flex",
                        documentStatusConfig[doc.status].color
                      )}
                    >
                      {documentStatusConfig[doc.status].label}
                    </Badge>

                    <Button
                      variant="ghost"
                      size="icon-lg"
                      onClick={() => handleDownload(doc)}
                      aria-label={`Download ${doc.originalFilename}`}
                      title="Download"
                    >
                      <FiDownload className="w-4 h-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon-lg"
                      onClick={() => setDeleteConfirmId(doc.id)}
                      aria-label={`Delete ${doc.originalFilename}`}
                      title="Delete"
                      className="text-destructive hover:text-destructive"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload modal */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Upload a document to your portal. Accepted formats: PDF, JPG, PNG,
              HEIC, DOCX, XLSX, CSV (max 10MB)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Drop zone */}
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
                isDragging
                  ? "border-accent bg-accent/5"
                  : "border-border hover:border-accent/50"
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {uploadFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FiFile className="w-8 h-8 text-primary" />
                  <div className="text-left">
                    <p className="font-medium truncate max-w-[200px]">
                      {uploadFile.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(uploadFile.size)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-lg"
                    onClick={() => setUploadFile(null)}
                    aria-label="Remove selected file"
                  >
                    <FiX className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <FiUpload className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
                  <p className="text-sm text-muted-foreground mb-2">
                    Drag and drop a file here, or
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Browse files
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.heic,.docx,.xlsx,.csv"
                    onChange={handleFileInputChange}
                  />
                </>
              )}
            </div>

            {/* Category select */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <select
                value={uploadCategory}
                onChange={(e) =>
                  setUploadCategory(e.target.value as DocumentCategory)
                }
                className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {documentCategoryLabels[cat]}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Description (optional)
              </label>
              <Input
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="Add a description..."
                maxLength={500}
              />
            </div>

            {/* Upload button */}
            <Button
              className="w-full btn-pill"
              onClick={handleUpload}
              disabled={!uploadFile || isUploading}
            >
              {isUploading ? (
                <>
                  <FiLoader className="animate-spin mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <FiUpload className="mr-2" />
                  Upload
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation modal */}
      <Dialog
        open={!!deleteConfirmId}
        onOpenChange={() => setDeleteConfirmId(null)}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this document? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setDeleteConfirmId(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
